import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Coffee } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";

const AMENITIES = [
  ["wifi", "Wi-Fi"],
  ["outdoor_seating", "Outdoor seating"],
  ["vegan_milk", "Vegan milk"],
  ["laptop_friendly", "Laptop friendly"],
  ["pet_friendly", "Pet friendly"],
  ["whole_bean", "Whole bean retail"],
  ["tasting_bar", "Tasting bar"],
  ["classes", "Brewing classes"],
];

const VIBES = [
  ["quiet_study", "Quiet / study"],
  ["first_date", "First date"],
  ["work_friendly", "Work-friendly"],
  ["espresso_bar", "Espresso bar"],
  ["family", "Family"],
  ["outdoor", "Outdoor"],
];

export default function SubmitCafePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    type: "cafe",
    description: "",
    address: "",
    city: "",
    country: "",
    latitude: "",
    longitude: "",
    image_url: "",
    hours: "",
    website: "",
    phone: "",
    price_range: "$$",
    amenities: [],
    vibe_tags: [],
    specialties: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleAmenity = (key) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(key)
        ? f.amenities.filter((a) => a !== key)
        : [...f.amenities, key],
    }));
  };

  const toggleVibe = (key) => {
    setForm((f) => ({
      ...f,
      vibe_tags: f.vibe_tags.includes(key)
        ? f.vibe_tags.filter((v) => v !== key)
        : [...f.vibe_tags, key],
    }));
  };

  if (user === false) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="submit-auth-gate">
        <Coffee className="h-7 w-7 text-[#C05A46] mx-auto" strokeWidth={1.5} />
        <h1 className="font-serif text-4xl mt-4">Sign in to submit a café</h1>
        <p className="text-[#786B63] mt-3">It only takes a second. We just want to know who's adding what.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button asChild className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]">
            <Link to="/login" state={{ from: "/submit" }}>Sign in</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm">
            <Link to="/register">Create account</Link>
          </Button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setError("Please provide valid latitude and longitude.");
      return;
    }
    if (!form.country) {
      setError("Please select a country.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        latitude: lat,
        longitude: lng,
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/cafes", payload);
      toast(
        data.status === "approved" ? "Thanks! Your café is live." : "Thanks! Submitted for review — we'll publish it shortly.",
        { description: data.name }
      );
      navigate(`/cafe/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="submit-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Community submission</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Submit a café or bean shop</h1>
      <p className="text-[#786B63] mt-3 max-w-xl">
        Share a place worth discovering. Be specific — what makes it special? Where exactly is it?
      </p>

      <form onSubmit={submit} className="mt-10 border border-border bg-card rounded-sm p-8 space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Label htmlFor="name">Name *</Label>
            <Input id="name" required value={form.name} onChange={change("name")}
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-name" />
          </div>
          <div>
            <Label>Type *</Label>
            <Select value={form.type} onValueChange={update("type")}>
              <SelectTrigger className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm bg-[#F9F6F0]">
                <SelectItem value="cafe">Café</SelectItem>
                <SelectItem value="bean_shop">Bean shop / Roastery</SelectItem>
                <SelectItem value="both">Both — café + bean shop</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Price</Label>
            <Select value={form.price_range} onValueChange={update("price_range")}>
              <SelectTrigger className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm bg-[#F9F6F0]">
                <SelectItem value="$">$ — affordable</SelectItem>
                <SelectItem value="$$">$$ — moderate</SelectItem>
                <SelectItem value="$$$">$$$ — premium</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="desc">Description *</Label>
            <Textarea id="desc" required rows={4} value={form.description} onChange={change("description")}
                      placeholder="What makes this place worth visiting?"
                      className="mt-1.5 rounded-sm bg-[#F9F6F0]" data-testid="submit-description" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="address">Street address *</Label>
            <Input id="address" required value={form.address} onChange={change("address")}
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-address" />
          </div>
          <div>
            <Label htmlFor="city">City *</Label>
            <CityAutocomplete
              value={form.city}
              country={form.country}
              onChange={(v) => setForm((f) => ({ ...f, city: v }))}
              onSelect={(c) => setForm((f) => ({
                ...f,
                city: c.name,
                country: c.country || f.country,
                latitude: c.latitude != null ? String(c.latitude) : f.latitude,
                longitude: c.longitude != null ? String(c.longitude) : f.longitude,
              }))}
              testId="submit-city-autocomplete"
            />
          </div>
          <div>
            <Label htmlFor="country">Country *</Label>
            <Select value={form.country} onValueChange={update("country")}>
              <SelectTrigger className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-country">
                <SelectValue placeholder="Select a country…" />
              </SelectTrigger>
              <SelectContent className="rounded-sm bg-[#F9F6F0]">
                <SelectItem value="Canada">Canada</SelectItem>
                <SelectItem value="USA">United States</SelectItem>
                <SelectItem value="UK">United Kingdom</SelectItem>
                <SelectItem value="Australia">Australia</SelectItem>
                <SelectItem value="New Zealand">New Zealand</SelectItem>
                <SelectItem value="Ireland">Ireland</SelectItem>
                <SelectItem value="Germany">Germany</SelectItem>
                <SelectItem value="France">France</SelectItem>
                <SelectItem value="Italy">Italy</SelectItem>
                <SelectItem value="Spain">Spain</SelectItem>
                <SelectItem value="Netherlands">Netherlands</SelectItem>
                <SelectItem value="Japan">Japan</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="lat">Latitude *</Label>
            <Input id="lat" required value={form.latitude} onChange={change("latitude")}
                   placeholder="e.g. 40.7438"
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-lat" />
          </div>
          <div>
            <Label htmlFor="lng">Longitude *</Label>
            <Input id="lng" required value={form.longitude} onChange={change("longitude")}
                   placeholder="e.g. -74.0028"
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" data-testid="submit-lng" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="image">Image URL</Label>
            <Input id="image" value={form.image_url} onChange={change("image_url")}
                   placeholder="https://…"
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" />
          </div>
          <div>
            <Label htmlFor="hours">Hours</Label>
            <Input id="hours" value={form.hours} onChange={change("hours")}
                   placeholder="Mon–Sun · 7am – 6pm"
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" />
          </div>
          <div>
            <Label htmlFor="website">Website</Label>
            <Input id="website" value={form.website} onChange={change("website")}
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="specialties">Specialties (comma separated)</Label>
            <Input id="specialties" value={form.specialties} onChange={change("specialties")}
                   placeholder="Single Origin, Pour Over, Cold Brew"
                   className="mt-1.5 rounded-sm bg-[#F9F6F0] h-11" />
          </div>
        </div>

        <div>
          <Label>Amenities</Label>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {AMENITIES.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={form.amenities.includes(k)}
                  onCheckedChange={() => toggleAmenity(k)}
                  data-testid={`amenity-${k}`}
                />
                {l}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Vibe tags</Label>
          <p className="text-xs text-[#786B63] mt-1">Pick whatever fits — helps people find their kind of place.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {VIBES.map(([k, l]) => {
              const on = form.vibe_tags.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleVibe(k)}
                  data-testid={`vibe-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    on
                      ? "border-[#C05A46] bg-[#C05A46] text-[#F9F6F0]"
                      : "border-border bg-[#F9F6F0] text-[#786B63] hover:border-[#C05A46]"
                  }`}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <p className="text-sm text-[#C05A46]" data-testid="submit-error">
            {typeof error === "string" ? error : "Please check the form."}
          </p>
        )}

        <Button
          type="submit"
          disabled={submitting}
          data-testid="submit-cafe-button"
          className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0] h-11 px-8"
        >
          {submitting ? "Submitting…" : "Submit café"}
        </Button>
      </form>
    </div>
  );
}
